import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Users, QrCode, TrendingUp, Zap } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { getUserGames, getUserCustomers } from '../../services/firestore';

const DashboardHome = () => {
  const { currentUser } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [growthData, setGrowthData] = useState([]);
  const [prizeData, setPrizeData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentUser) {
      fetchDashboardData();
    }
  }, [currentUser]);

  const fetchDashboardData = async () => {
    if (!currentUser) return;
    
    try {
      // Fetch games and customers from Firestore
      const [games, customers] = await Promise.all([
        getUserGames(currentUser.uid),
        getUserCustomers(currentUser.uid)
      ]);

      // Calculate metrics
      const totalScans = games.reduce((sum, game) => sum + (game.total_scans || 0), 0);
      const totalPlays = games.reduce((sum, game) => sum + (game.total_plays || 0), 0);
      
      // Calculate new customers this week/month
      const now = new Date();
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      
      const newCustomersWeek = customers.filter(c => {
        const createdAt = c.created_at?.toDate ? c.created_at.toDate() : new Date(c.created_at);
        return createdAt >= oneWeekAgo;
      }).length;
      
      const newCustomersMonth = customers.filter(c => {
        const createdAt = c.created_at?.toDate ? c.created_at.toDate() : new Date(c.created_at);
        return createdAt >= oneMonthAgo;
      }).length;

      setMetrics({
        total_scans: totalScans,
        total_participants: customers.length,
        new_customers_week: newCustomersWeek,
        new_customers_month: newCustomersMonth
      });

      // Generate mock growth data for now
      setGrowthData([
        { date: 'Lun', customers: 12 },
        { date: 'Mar', customers: 19 },
        { date: 'Mer', customers: 15 },
        { date: 'Jeu', customers: 25 },
        { date: 'Ven', customers: 22 },
        { date: 'Sam', customers: 30 },
        { date: 'Dim', customers: 28 }
      ]);

      // Calculate prize distribution
      const prizeCount = {};
      customers.forEach(c => {
        prizeCount[c.prize_won] = (prizeCount[c.prize_won] || 0) + 1;
      });
      
      const prizeDistribution = Object.entries(prizeCount).map(([name, value]) => ({
        name,
        value
      }));
      
      setPrizeData(prizeDistribution);
      
    } catch (error) {
      console.error('Erreur lors du chargement des données du tableau de bord:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const statCards = metrics ? [
    { icon: <QrCode className="w-8 h-8" />, label: 'Total Scans QR', value: metrics.total_scans, color: 'from-[#0082FB] to-[#2E59D7]' },
    { icon: <Users className="w-8 h-8" />, label: 'Total Participants', value: metrics.total_participants, color: 'from-[#0061A0] to-[#0082FB]' },
    { icon: <TrendingUp className="w-8 h-8" />, label: 'Nouveaux Cette Semaine', value: metrics.new_customers_week, color: 'from-green-500 to-green-600' },
    { icon: <Zap className="w-8 h-8" />, label: 'Nouveaux Ce Mois', value: metrics.new_customers_month, color: 'from-purple-500 to-purple-600' }
  ] : [];

  const COLORS = ['#0891b2', '#14b8a6', '#22c55e', '#f59e0b', '#ef4444'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#2E59D7]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-home">
      {/* Welcome Message */}
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Bienvenue !</h2>
        <p className="text-gray-600">Voici ce qui se passe avec votre entreprise aujourd'hui</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <Card key={index} className="p-6 hover:shadow-lg transition-shadow" data-testid={`stat-card-${index}`}>
            <div className={`w-16 h-16 bg-gradient-to-br ${stat.color} rounded-2xl flex items-center justify-center text-white mb-4`}>
              {stat.icon}
            </div>
            <p className="text-sm text-gray-600 mb-1">{stat.label}</p>
            <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Customer Growth Chart */}
        <Card className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Croissance Clients</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={growthData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke="#0891b2" strokeWidth={3} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        {/* Prize Distribution Chart */}
        <Card className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Distribution des Prix</h3>
          {prizeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={prizeData}
                  dataKey="count"
                  nameKey="prize"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label
                >
                  {prizeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              <p>Aucune donnée de prix pour le moment</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default DashboardHome;